import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material';
import { AddPhotoAlternate as PhotoIcon, Close as CloseIcon } from '@mui/icons-material';

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/**
 * Optional photo for rewards / promotions: drop zone / preview with replace + remove. `value` is
 * { file, url, removed } — the file is uploaded after the record is saved.
 */
const LoyaltyImagePicker = ({ value, onChange, disabled, t }) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const preview = value.file ? URL.createObjectURL(value.file) : (!value.removed && value.url) || null;

  useEffect(() => () => { if (value.file && preview) URL.revokeObjectURL(preview); }, [value.file, preview]);

  const pick = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { onChange({ ...value, error: t('loyalty.imageInvalid') }); return; }
    if (file.size > MAX_IMAGE_BYTES) { onChange({ ...value, error: t('loyalty.imageTooLarge') }); return; }
    onChange({ ...value, file, removed: false, error: null });
  };

  return (
    <Box>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
      {preview ? (
        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', height: 160, bgcolor: 'grey.100' }}>
          <Box component="img" src={preview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          {!disabled && (
            <Stack direction="row" spacing={1} sx={{ position: 'absolute', right: 8, bottom: 8 }}>
              <Button size="small" variant="contained" color="inherit" startIcon={<PhotoIcon />} onClick={() => inputRef.current?.click()} sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                {t('loyalty.imageReplace')}
              </Button>
              <IconButton size="small" onClick={() => onChange({ file: null, url: value.url, removed: true, error: null })} sx={{ bgcolor: 'rgba(255,255,255,0.92)' }} title={t('loyalty.imageRemove')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) pick(e.dataTransfer.files?.[0]); }}
          sx={{
            height: 120,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: dragging ? 'primary.main' : alpha(theme.palette.primary.main, 0.35),
            bgcolor: dragging ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.04),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            cursor: disabled ? 'default' : 'pointer',
            color: 'text.secondary',
            transition: 'all .15s',
          }}
        >
          <PhotoIcon color="primary" />
          <Typography variant="body2" fontWeight={600} color="text.primary">{t('loyalty.imageAdd')}</Typography>
          <Typography variant="caption">{t('loyalty.imageHint')}</Typography>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{t('loyalty.imageSize')}</Typography>
      {value.error && <Typography variant="caption" color="error" display="block" mt={0.5}>{value.error}</Typography>}
    </Box>
  );
};

export default LoyaltyImagePicker;
